import { releaseCachedTextureBase, releaseTexture } from '@utils/sceneObjects/loadedTextureCache'
import { Material, Mesh, Object3D, Texture } from 'three'
import { isLightweightViewer } from '../../viewport/readCapabilities'

export const GLB_INSTANCE_OWNED_GEOMETRY = 'glbInstanceOwnedGeometry'

/**
 * Clona geometrías de meshes para no mutar/disponer el buffer compartido del sceneGLB cache.
 * Object3D.clone() reutiliza la misma BufferGeometry en todos los ítems.
 * Lightweight: share the catalog buffers. morphTargetInfluences live on the Mesh,
 * so morphs stay correct without doubling VRAM per unit.
 */
export function cloneGlbMeshGeometries(root: Object3D): void {
  if (isLightweightViewer()) return
  root.traverse((node) => {
    const mesh = node as Mesh
    if (!mesh.isMesh || !mesh.geometry) return
    mesh.geometry = mesh.geometry.clone()
    // three@0.160 shares userData by reference on clone(); isolate or flags leak
    // to the catalog cache and every other unit using this GLB.
    mesh.geometry.userData = { ...mesh.geometry.userData, [GLB_INSTANCE_OWNED_GEOMETRY]: true }
  })
}

/** Dispose a cached GLB source after its React clones have unmounted. */
export function disposeGlbSource(root: Object3D) {
  root.traverse((node) => {
    const mesh = node as Mesh
    if (!mesh.isMesh) return
    mesh.geometry?.dispose()
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    for (const mat of materials) {
      if (mat) disposeMaterial(mat)
    }
  })
}

/**
 * Libera geometrías, texturas de material y materiales de un clone de useGLBInstance.
 */
export function disposeGlbClone(root: Object3D): void {
  root.traverse((node) => {
    const mesh = node as Mesh
    if (!mesh.isMesh) return

    if (mesh.geometry?.userData?.[GLB_INSTANCE_OWNED_GEOMETRY]) {
      mesh.geometry.dispose()
      delete mesh.geometry.userData[GLB_INSTANCE_OWNED_GEOMETRY]
    }

    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    for (const mat of materials) {
      if (mat) disposeMaterial(mat)
    }
  })
}

function disposeMaterial(material: Material): void {
  const map = material.map
  const base = material.userData?.currentTextureBase as Texture | undefined
  releaseCachedTextureBase(material)
  if (map && map !== base) {
    releaseTexture(map)
  }
  material.dispose()
}
